import { Group, LoopOnce, sRGBEncoding, TextureLoader, Vector2 } from 'three';

export class Animator {
  constructor(scene, loadedModel, state, controls, mixer, clips) {
    window.toggleSound = this.toggleSound.bind(this);

    this.scene = scene;
    this.loadedModel = loadedModel;
    this.state = state;
    this.controls = controls;
    this.mixer = mixer;
    this.clips = clips;

    this.hasInitialized = false;
    this.isSoundEnable = true;

    this.animateParam = {
      state: 'Standby',
      currentState: 'Standby',
      originY: 0,
      floorFrom: 1,
      floor: 1,
      floorTarget: 1,
      floorQueue: {
        que: new Map(),
        inverse: new Map(),
      },
      onInverse: false,
      floorDistance: 5,
      distance: 0,
      movedDistance: 0,
      checkPoints: [],
      speed: 0,
      acceleration: 0.3,
      hasPlayedSound: false,
      currentAudioEl: null,
      playedSounds: new Set(),
      openedTime: 0,
      onButtons: [],
      indicatorDirection: 'Up',
      indicatorColor: 'Black',
      indicatorLng: 'JP',
      indicatorTimer: 0,
      indicatorIsEncho: false,
      indicatorIconTimer: 0,
      indicatorIconIndex: 1,
      indicatorDoorOpenShown: false,
      doorOpenDelayTimer: 0,
      messageOpenDelay: 2.1,
      petButton: {
        isEnabled: false,
        isFlicker: false,
        activateTimer: 0,
        flickerTimer: 0,
        petMultiplyer: 2,
      },
      isLanternOn: false,
      lanternColor: 'Orange',
      doorSignal: {
        isEnabled: false,
        isActive: false,
        blinkTimer: 0,
        activeTimer: 0,
      },
      emergencyButton: {
        isEnabled: false,
        activeTimer: 0
      },
    };
  }

  //////////
  // Cage
  //////////
  animateCage(dt) {
    // State changed
    if (this.animateParam.state != this.animateParam.currentState) {
      this.onCageStateChanged();
    }

    // Check per frame
    this.onCagePerFrame(dt);

    // Restore buttons material
    this.restoreButton();

    // Update indicator Lng
    if (this.animateParam.indicatorTimer >= 1.5) {
      this.animateParam.indicatorTimer = 0;
      (async () => {
        await this.updateCageIndicatorLng();
      })();
    }

    // Update indicator Icon (3秒周期)
    if (this.animateParam.indicatorIconTimer >= 3) {
      this.animateParam.indicatorIconTimer = 0;
      this.animateParam.indicatorIconIndex =
        (this.animateParam.indicatorIconIndex % 4) + 1;
      (async () => {
        await this.updateCageIndicatorIcon();
      })();
    }
  }

  //
  // Cage Animation Controls
  //
  onCageStateChanged() {
    switch (this.animateParam.state) {
      case 'Open': {
        this.animateParam.indicatorDoorOpenShown = false;
        this.animateParam.doorOpenDelayTimer = 0;
        let cageOpen = this.mixer.clipAction(this.clips[0]);
        let cageClose = this.mixer.clipAction(this.clips[1]);
        let hallOpen =
          this.loadedModel.type == 'R9'
            ? this.mixer.clipAction(this.clips[3])
            : this.mixer.clipAction(this.clips[2]);
        let hallClose =
          this.loadedModel.type == 'R9'
            ? this.mixer.clipAction(this.clips[2])
            : this.mixer.clipAction(this.clips[3]);

        // Reset Close Animation if finished
        [cageClose, hallClose].forEach((action) => {
          if (action.paused) {
            action.stop();
            this.state.actionStates[action.getClip().name] = false;
            this.animateParam.playedSounds.delete(
              'assets/audios/EQUA14_4_Door_Open.wav'
            );
          }
        });

        // Play Open Animation
        [cageOpen, hallOpen].forEach((action) => {
          action.setLoop(LoopOnce);
          action.clampWhenFinished = true;
          action.reset().play();
          this.state.actionStates[action.getClip().name] = true;

          // On closing doors
          if (action == cageOpen && cageClose.isRunning()) {
            cageClose.crossFadeTo(action, 1.3);
            this.state.actionStates[cageClose.getClip().name] = false;
          } else if (action == hallOpen && hallClose.isRunning()) {
            hallClose.crossFadeTo(action, 1.3);
            this.state.actionStates[hallClose.getClip().name] = false;

            // Deactivate door signal
            this.animateParam.doorSignal.isActive = false;
          }
        });

        this.playSound('assets/audios/EQUA14_89_Door_Open.wav', true);
        (async () => {
          await this.updateCageIndicatorMessage();
        })();
        break;
      }

      case 'Close': {
        let cageOpen = this.mixer.clipAction(this.clips[0]);
        let cageClose = this.mixer.clipAction(this.clips[1]);
        let hallOpen =
          this.loadedModel.type == 'R9'
            ? this.mixer.clipAction(this.clips[3])
            : this.mixer.clipAction(this.clips[2]);
        let hallClose =
          this.loadedModel.type == 'R9'
            ? this.mixer.clipAction(this.clips[2])
            : this.mixer.clipAction(this.clips[3]);

        // Reset Open Animation if finished
        [cageOpen, hallOpen].forEach((action) => {
          if (action.paused) {
            action.stop();
            this.state.actionStates[action.getClip().name] = false;
            this.animateParam.playedSounds.delete(
              'assets/audios/EQUA14_4_Door_Close.wav'
            );
          }
        });

        // Play Close Animation
        [cageClose, hallClose].forEach((action) => {
          action.setLoop(LoopOnce);
          action.clampWhenFinished = true;
          action.reset().play();
          this.state.actionStates[action.getClip().name] = true;

          if (action == cageClose && cageOpen.isRunning()) {
            cageOpen.crossFadeTo(action, 1.3);
            this.state.actionStates[cageOpen.getClip().name] = false;
          } else if (action == hallClose && hallOpen.isRunning()) {
            hallOpen.crossFadeTo(action, 1.3);
            this.state.actionStates[hallOpen.getClip().name] = false;
          }
        });

        // Activate door signal
        this.animateParam.doorSignal.isActive = true;
        this.animateParam.doorSignal.activeTimer = 0;

        this.playSound('assets/audios/EQUA14_4_Door_Close.wav', true);
        (async () => {
          await this.updateCageIndicatorMessage();
        })();
        break;
      }
    }

    this.animateParam.currentState = this.animateParam.state;
  }

  onCagePerFrame(dt) {
    if (this.animateParam.state == 'Move') {
      const hall = this.scene.getObjectByName('Hall');
      const hallWrap = this.scene.getObjectByName('HallWrap');

      // Accelerate
      if (Math.abs(this.animateParam.movedDistance) < 1.5)
        this.animateParam.speed += dt * this.animateParam.acceleration;
      // Decelerate
      else if (
        Math.abs(this.animateParam.distance) - 1.5 <
        Math.abs(this.animateParam.movedDistance)
      ) {
        this.animateParam.speed -= dt * this.animateParam.acceleration;

        // Handle arriving audios
        if (!this.animateParam.hasPlayedSound) {
          let audioEl;
          switch (this.animateParam.floorTarget) {
            case 1: {
              audioEl = this.playSound('assets/audios/EQUA14_13_1F.wav');
              break;
            }
            case 2: {
              audioEl = this.playSound('assets/audios/EQUA14_14_2F.wav');
              break;
            }
            case 3: {
              audioEl = this.playSound('assets/audios/EQUA14_15_3F.wav');
              break;
            }
            case 4: {
              audioEl = this.playSound('assets/audios/EQUA14_16_4F.wav');
              break;
            }
            case 5: {
              audioEl = this.playSound('assets/audios/EQUA14_17_5F.wav');
              break;
            }
            case 6: {
              audioEl = this.playSound('assets/audios/EQUA14_18_6F.wav');
              break;
            }
            case 7: {
              audioEl = this.playSound('assets/audios/EQUA14_19_7F.wav');
              break;
            }
            case 8: {
              audioEl = this.playSound('assets/audios/EQUA14_20_8F.wav');
              break;
            }
            case 9: {
              audioEl = this.playSound('assets/audios/EQUA14_21_9F.wav');
              break;
            }
            case 10: {
              audioEl = this.playSound('assets/audios/EQUA14_22_10F.wav');
              break;
            }
            case 11: {
              audioEl = this.playSound('assets/audios/EQUA14_23_11F.wav');
              break;
            }
            case 12: {
              audioEl = this.playSound('assets/audios/EQUA14_24_12F.wav');
              break;
            }
          }
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_89_Door_Open.wav'
          );
          audioEl.addEventListener('ended', () => {
            this.playSound('assets/audios/EQUA14_89_Door_Open.wav', true);
          });
          this.animateParam.hasPlayedSound = true;

          // Update indicator
          (async () => {
            await this.updateCageIndicatorFloor(this.animateParam.floorTarget);
          })();
        }
      }

      // Move
      hall.position.y -=
        dt * this.animateParam.speed * Math.sign(this.animateParam.distance);
      hallWrap.position.y -=
        dt * this.animateParam.speed * Math.sign(this.animateParam.distance);
      this.animateParam.movedDistance +=
        dt * this.animateParam.speed * Math.sign(this.animateParam.distance);
      if (
        Math.abs(this.animateParam.movedDistance) >=
        this.animateParam.checkPoints[0]
      ) {
        this.animateParam.floor +=
          Math.sign(this.animateParam.movedDistance) * 1;
        this.animateParam.checkPoints.shift();
        (async () => {
          await this.updateCageIndicatorFloor(this.animateParam.floor);
        })();
      }

      // Animate indicator
      this.scene
        .getObjectByName(this.loadedModel.type + '_Indicator')
        .traverseVisible((obj) => {
          if (obj.name.includes('Arrow')) {
            if (obj.material.map.offset.y > 0.9)
              obj.material.map.offset.y = -0.9;
            obj.material.map.offset.y += dt * 1;
            obj.material.map.flipY = false;
            obj.material.needsUpdate = true;
          }
        });

      // Stopped
      if (
        this.animateParam.speed <= 0 ||
        Math.abs(this.animateParam.movedDistance) >=
        Math.abs(this.animateParam.distance)
      ) {
        this.animateParam.floor = this.animateParam.floorTarget;
        this.animateParam.speed = 0;
        this.animateParam.movedDistance = 0;
        this.animateParam.checkPoints = [];
        this.animateParam.hasPlayedSound = false;

        // Restore button material
        const btnObj = this.scene.getObjectByName(
          this.animateParam.floorQueue.que.get(this.animateParam.floor)
        );
        const offMat = this.scene.getMaterialByName(
          btnObj.material.name.replace('_on', '')
        );
        if (!offMat) {
          console.warn(
            'Material not found: ' + btnObj.material.name.replace('_on', '')
          );
          return;
        }
        btnObj.material = offMat;
        btnObj.material.needsUpdate = true;

        // Delete queue
        this.animateParam.floorQueue.que.delete(this.animateParam.floor);

        // Delete hall
        hall.position.y = this.animateParam.originY;
        hallWrap.clear(); // ToDo: memory performance
        hallWrap.removeFromParent();

        // Update queue to inverse queue if it needs
        if (
          this.animateParam.floorQueue.que.size == 0 &&
          this.animateParam.floorQueue.inverse.size > 0
        ) {
          this.animateParam.floorQueue.que =
            this.animateParam.floorQueue.inverse;
          if (this.animateParam.indicatorDirection == 'Up') {
            this.animateParam.indicatorDirection = 'Down';
            this.animateParam.onInverse = true;
            this.animateParam.floorQueue.que = sortMapByKey(
              this.animateParam.floorQueue.que,
              true
            );
          } else {
            this.animateParam.indicatorDirection = 'Up';
            this.animateParam.onInverse = true;
            this.animateParam.floorQueue.que = sortMapByKey(
              this.animateParam.floorQueue.que
            );
          }
          this.animateParam.floorQueue.inverse = new Map();
        }

        // Reset indicator
        (async () => {
          await this.setCageIndicator();
        })();

        this.animateParam.state = 'Open';
      }
    } else if (this.animateParam.state == 'Open') {
      // Auto close timer
      this.animateParam.openedTime += dt;
      this.animateParam.doorOpenDelayTimer += dt;

      // DoorOpening → DoorOpen 切替
      if (
        !this.animateParam.indicatorDoorOpenShown &&
        this.animateParam.doorOpenDelayTimer >= this.animateParam.messageOpenDelay
      ) {
        this.animateParam.indicatorDoorOpenShown = true;
        (async () => {
          const matName = this.animateParam.indicatorIsEncho
            ? 'M_Indicator_Encho_Message' : 'M_Indicator_Message';
          const msg = this.scene.getMaterialByName(matName);
          const enchoPrefix = this.animateParam.indicatorIsEncho ? 'Encho_' : '';
          const color = this.animateParam.indicatorColor;
          const path = 'assets/images/Indicator_MainPanel/' + color + '/' +
            enchoPrefix + 'DoorOpen_' + color + '.png';
          msg.map = await new TextureLoader().loadAsync(path);
          msg.map.name = path;
          msg.map.flipY = false;
          msg.map.encoding = sRGBEncoding;
          msg.needsUpdate = true;
        })();
      }

      if (this.animateParam.openedTime > 6) {
        this.animateParam.state = 'Close';
      }
      // Activate door signal
      else if (
        this.animateParam.openedTime > 5 &&
        !this.animateParam.doorSignal.isActive
      ) {
        this.animateParam.doorSignal.isActive = true;
      } else if (
        this.animateParam.openedTime > 3 &&
        this.animateParam.onInverse
      ) {
        if (this.animateParam.indicatorDirection == 'Up') {
          this.playSound('assets/audios/EQUA14_5_Up.wav', true);
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_6_Down.wav'
          );
        } else {
          this.playSound('assets/audios/EQUA14_6_Down.wav', true);
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_5_Up.wav'
          );
        }
        this.animateParam.onInverse = false;
      }
    } else if (this.animateParam.state == 'Close') {
      this.animateParam.openedTime = 0;

      // Change state to "Move" when close animation finished
      let cageClose = this.mixer.clipAction(this.clips[1]);
      let hallClose =
        this.loadedModel.type == 'R9'
          ? this.mixer.clipAction(this.clips[2])
          : this.mixer.clipAction(this.clips[3]);
      if (cageClose.paused && hallClose.paused) {
        if (this.animateParam.floorQueue.que.size > 0) {
          // Will Move
          this.animateParam.floorFrom = this.animateParam.floor;
          this.animateParam.indicatorIsEncho = false;

          const key = [...this.animateParam.floorQueue.que][0][0];
          this.animateParam.floorTarget = key;
          let distance =
            this.animateParam.floorTarget - this.animateParam.floor;

          // Update indicator
          (async () => {
            await this.setCageIndicator();
            await this.updateCageIndicatorMessage(
              this.animateParam.floorTarget
            );
          })();

          // Duplicate hall
          const hallWrap = new Group();
          hallWrap.name = 'HallWrap';
          this.scene.getObjectByName('EV').add(hallWrap);

          const hall = this.scene.getObjectByName('Hall');

          for (let i = 0; i < Math.abs(distance); i++) {
            let clone = hall.clone();
            clone.name = 'Hall' + i;
            hallWrap.add(clone);
            clone.position.copy(hall.position);
            clone.position.y +=
              (i + 1) * (Math.sign(distance) * this.animateParam.floorDistance);

            this.animateParam.checkPoints.push(
              (i + 1) * this.animateParam.floorDistance - 1
            );
          }

          this.animateParam.state = 'Move';
          this.animateParam.distance =
            distance * this.animateParam.floorDistance;
          this.animateParam.originY = hall.position.y;
          (async () => {
            await this.updateCageSideIndicators();
          })();
        } else {
          // Not Move
          this.animateParam.state = 'Standby';
          this.animateParam.indicatorIsEncho = false;
          this.animateParam.playedSounds = new Set();
          (async () => {
            await this.setCageIndicator();
            await this.updateCageSideIndicators();
          })();
        }
      }
    }

    // Pet button controller
    if (this.animateParam.petButton.isEnabled) {
      let buttonObj;
      this.scene
        .getObjectByName('22_MainPanel_Button')
        .traverseVisible((obj) => {
          if (obj.name.match(/_[Pp]et_([Ss]ingle|[Tt]win)$/g)) {
            buttonObj = obj;
          }
        });

      // Flickerd
      if (this.animateParam.petButton.isFlicker) {
        this.animateParam.petButton.flickerTimer += dt;
        this.animateParam.petButton.activateTimer += dt;

        // OFF
        if (this.animateParam.petButton.flickerTimer > 0.666) {
          this.animateParam.petButton.flickerTimer = 0;
          const offMat = this.scene.getMaterialByName(
            buttonObj.material.name.replace('_on', '')
          );
          if (offMat) {
            buttonObj.material = offMat;
            buttonObj.material.needsUpdate = true;
          }
        }
        // ON
        else if (this.animateParam.petButton.flickerTimer > 0.333) {
          const onMat = this.scene.getMaterialByName(
            buttonObj.material.name.split('.')[0] + '_on'
          );
          if (onMat) {
            buttonObj.material = onMat;
            buttonObj.material.emissiveIntensity = 4;
            buttonObj.material.needsUpdate = true;
          }
        }

        // Activate
        if (this.animateParam.petButton.activateTimer > 5) {
          // No queue
          if (this.animateParam.floorQueue.que.size == 0) {
            const offMat = this.scene.getMaterialByName(
              buttonObj.material.name.replace('_on', '')
            );
            if (offMat) {
              buttonObj.material = offMat;
              buttonObj.material.needsUpdate = true;
            }

            this.animateParam.petButton.isEnabled = false;
            this.animateParam.petButton.isFlicker = false;
            this.animateParam.petButton.activateTimer = 0;
            this.animateParam.petButton.flickerTimer = 0;

            return;
          }

          // ON
          const onMat = this.scene.getMaterialByName(
            buttonObj.material.name.split('.')[0] + '_on'
          );
          if (onMat) {
            buttonObj.material = onMat;
            buttonObj.material.emissiveIntensity = 4;
            buttonObj.material.needsUpdate = true;
          }

          this.animateParam.petButton.isFlicker = false;
          this.animateParam.petButton.activateTimer = 0;
          this.animateParam.petButton.flickerTimer = 0;

          this.mixer.timeScale = 1 / this.animateParam.petButton.petMultiplyer;
        }
      }
      // No queues
      else if (
        this.animateParam.floorQueue.que.size == 0 &&
        this.animateParam.floorQueue.inverse.size == 0
      ) {
        const offMat = this.scene.getMaterialByName(
          buttonObj.material.name.replace('_on', '')
        );
        if (offMat) {
          buttonObj.material = offMat;
          buttonObj.material.needsUpdate = true;
        }

        this.animateParam.petButton.isEnabled = false;
        this.animateParam.petButton.isFlicker = false;
        this.animateParam.petButton.activateTimer = 0;
        this.animateParam.petButton.flickerTimer = 0;
        this.mixer.timeScale = 1;
        return;
      }
    }

    // Door signal controller
    this.updateDoorSignal(dt);

    // Emergency button controller
    if (this.animateParam.emergencyButton.isEnabled)
      this.updateEmergency(dt);
  }

  //
  // Cage Animation Functions
  //
  async setCageIndicator() {
    const color = this.animateParam.indicatorColor;
    const basePath = 'assets/images/Indicator_MainPanel/' + color + '/';
    const enchoPrefix = this.animateParam.indicatorIsEncho ? 'Encho_' : '';

    // Mainpanel — Arrow, Floor, Message
    const arrowMatName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Arrow' : 'M_Indicator_Arrow';
    const floorMatName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Floor' : 'M_Indicator_Floor';
    const messageMatName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Message' : 'M_Indicator_Message';

    const arrow = this.scene.getMaterialByName(arrowMatName);
    const floor = this.scene.getMaterialByName(floorMatName);
    const message = this.scene.getMaterialByName(messageMatName);
    if (!arrow || !floor || !message)
      console.error('Indicator materials are not found.');

    // Arrow
    const arrowPath = basePath + enchoPrefix + 'Arrow_' + color + '.png';
    arrow.map = await new TextureLoader().loadAsync(arrowPath);
    arrow.map.name = arrowPath;
    arrow.map.flipY = false;
    arrow.map.encoding = sRGBEncoding;
    arrow.map.center = new Vector2(0.5, 0.5);
    if (this.animateParam.indicatorDirection == 'Down')
      arrow.map.rotation = Math.PI;
    else arrow.map.rotation = 0;
    arrow.needsUpdate = true;

    // Floor
    const floorPath = basePath + enchoPrefix + 'Floor_' +
      String(this.animateParam.floor).padStart(2, '0') + 'F_' + color + '.png';
    floor.map = await new TextureLoader().loadAsync(floorPath);
    floor.map.name = floorPath;
    floor.map.flipY = false;
    floor.map.encoding = sRGBEncoding;
    floor.needsUpdate = true;

    // Message — stateに応じたテクスチャを設定
    let messagePath;
    if (this.animateParam.state == 'Open') {
      if (this.animateParam.doorOpenDelayTimer >= this.animateParam.messageOpenDelay) {
        messagePath = basePath + enchoPrefix + 'DoorOpen_' + color + '.png';
      } else {
        messagePath = basePath + enchoPrefix + 'DoorOpening_' + color + '.png';
      }
    } else if (this.animateParam.state == 'Close') {
      messagePath = basePath + enchoPrefix + 'DoorClosing_' + color + '.png';
    } else {
      // Move / Standby → Next_XF
      messagePath = basePath + enchoPrefix + 'Next_' +
        String(this.animateParam.floorTarget).padStart(2, '0') + 'F_' +
        this.animateParam.indicatorLng + '_' + color + '.png';
    }
    message.map = await new TextureLoader().loadAsync(messagePath);
    message.map.name = messagePath;
    message.map.flipY = false;
    message.map.encoding = sRGBEncoding;
    message.needsUpdate = true;

    // Icon
    const icon = this.scene.getMaterialByName('M_Mon_Icon');
    if (icon) {
      const iconPath = basePath + 'Icon_' +
        String(this.animateParam.indicatorIconIndex).padStart(2, '0') +
        '_' + color + '.png';
      icon.map = await new TextureLoader().loadAsync(iconPath);
      icon.map.name = iconPath;
      icon.map.flipY = false;
      icon.map.encoding = sRGBEncoding;
      icon.needsUpdate = true;
    }

    const cover = this.scene.getMaterialByName('M_CoverGlass');
    if (this.loadedModel.component == 'Cage' && cover) cover.opacity = 0;

    const indicator = this.scene.getObjectByName(
      this.loadedModel.type + '_Indicator'
    );

    // hide objects
    indicator.children.forEach((ind) => {
      ind.visible = true;

      // "Up"/"Down": indicatorDirectionと一致しない方を非表示
      const dir = this.animateParam.indicatorDirection;
      if (dir === 'Up' && ind.name.includes('Down')) ind.visible = false;
      if (dir === 'Down' && ind.name.includes('Up')) ind.visible = false;

      // "Encho": indicatorIsEnchoに応じて切り替え（Iconは常に表示）
      if (this.animateParam.indicatorIsEncho) {
        if (!ind.name.includes('Encho') && !ind.name.includes('Icon'))
          ind.visible = false;
      } else {
        if (ind.name.includes('Encho')) ind.visible = false;
      }
    });

    // Sidepanel
    this.updateCageSideIndicators();
  }

  async updateCageIndicatorLng() {
    if (
      !this.scene.getObjectByName('EV') ||
      this.state.pause ||
      !this.controls.enabled
    )
      return;

    const currentLng = this.animateParam.indicatorLng;
    this.animateParam.indicatorLng = currentLng == 'JP' ? 'EN' : 'JP';

    const matName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Message' : 'M_Indicator_Message';
    const message = this.scene.getMaterialByName(matName);

    // Next_XFテクスチャの場合のみ言語切替
    if (message.map.name.includes('Next_')) {
      const path = message.map.name.replace(
        '_' + currentLng + '_',
        '_' + this.animateParam.indicatorLng + '_'
      );
      message.map = await new TextureLoader().loadAsync(path);
      message.map.name = path;
      message.map.flipY = false;
      message.map.encoding = sRGBEncoding;
      message.needsUpdate = true;
    }
  }

  async updateCageIndicatorMessage(targetFloor = null) {
    const matName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Message' : 'M_Indicator_Message';
    const message = this.scene.getMaterialByName(matName);
    const enchoPrefix = this.animateParam.indicatorIsEncho ? 'Encho_' : '';
    const color = this.animateParam.indicatorColor;
    let path;

    if (targetFloor) {
      path = 'assets/images/Indicator_MainPanel/' + color + '/' +
        enchoPrefix + 'Next_' +
        String(targetFloor).padStart(2, '0') + 'F_' +
        this.animateParam.indicatorLng + '_' + color + '.png';
    } else {
      if (this.animateParam.state == 'Open') {
        path = 'assets/images/Indicator_MainPanel/' + color + '/' +
          enchoPrefix + 'DoorOpening_' + color + '.png';
      } else if (this.animateParam.state == 'Close') {
        path = 'assets/images/Indicator_MainPanel/' + color + '/' +
          enchoPrefix + 'DoorClosing_' + color + '.png';
      }
    }

    message.map = await new TextureLoader().loadAsync(path);
    message.map.name = path;
    message.map.flipY = false;
    message.map.encoding = sRGBEncoding;
    message.needsUpdate = true;
  }

  async updateCageIndicatorFloor(floorNum = this.animateParam.floor) {
    // Mainpanel
    const color = this.animateParam.indicatorColor;
    const enchoPrefix = this.animateParam.indicatorIsEncho ? 'Encho_' : '';
    const matName = this.animateParam.indicatorIsEncho
      ? 'M_Indicator_Encho_Floor' : 'M_Indicator_Floor';
    const floor = this.scene.getMaterialByName(matName);
    const floorPath = 'assets/images/Indicator_MainPanel/' + color + '/' +
      enchoPrefix + 'Floor_' +
      String(floorNum).padStart(2, '0') + 'F_' + color + '.png';
    floor.map = await new TextureLoader().loadAsync(floorPath);
    floor.map.name = floorPath;
    floor.map.flipY = false;
    floor.map.encoding = sRGBEncoding;
    floor.needsUpdate = true;

    // Sidepanel
    await this.updateCageSideIndicators(floorNum);
  }

  async updateCageIndicatorIcon() {
    const icon = this.scene.getMaterialByName('M_Mon_Icon');
    if (!icon) return;

    const color = this.animateParam.indicatorColor;
    const path = 'assets/images/Indicator_MainPanel/' + color + '/Icon_' +
      String(this.animateParam.indicatorIconIndex).padStart(2, '0') +
      '_' + color + '.png';
    icon.map = await new TextureLoader().loadAsync(path);
    icon.map.name = path;
    icon.map.flipY = false;
    icon.map.encoding = sRGBEncoding;
    icon.needsUpdate = true;
  }

  async updateCageSideIndicators(floorNum = this.animateParam.floor) {
    const arrow = this.scene.getMaterialByName('T_Indicator_Side_Arrow');
    const floor = this.scene.getMaterialByName('T_Indicator_Side_Floor');

    if (this.animateParam.state == 'Standby') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Off.png'
      );
      map.flipY = false;
      arrow.map = map;
    } else if (this.animateParam.indicatorDirection == 'Up') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Arrow_Up.png'
      );
      map.flipY = false;
      arrow.map = map;
    } else {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Arrow_Down.png'
      );
      map.flipY = false;
      arrow.map = map;
    }
    arrow.needsUpdate = true;

    const map = await new TextureLoader().loadAsync(
      'assets/images/Indicator_SidePanel/Indicator_Side_' +
      String(floorNum).padStart(2, '0') +
      '.png'
    );
    map.flipY = false;
    floor.map = map;
    floor.needsUpdate = true;
  }

  async onCageIntersect(intersect) {
    const floorstr = intersect.object.name.match(/[0-9]{1,2}(.?|_Center)$/g);
    let targetFloor;

    // Floor Buttons
    if (floorstr) {
      targetFloor = Number(floorstr.pop().replace('_Center', ''));
      const floorDiff = targetFloor - this.animateParam.floor;

      // Will move the cage
      if (
        floorDiff != 0 ||
        (floorDiff == 0 && this.animateParam.state == 'Move')
      ) {
        // Already has queue
        if (this.animateParam.floorQueue.que.size > 0) {
          if (this.animateParam.state == 'Move') {
            // Avoid queing too close floor
            if (this.animateParam.indicatorDirection == 'Up') {
              if (targetFloor > this.animateParam.floor + 1) {
                this.animateParam.floorQueue.que.set(
                  targetFloor,
                  intersect.object.name
                );
              } else {
                this.animateParam.floorQueue.inverse.set(
                  targetFloor,
                  intersect.object.name
                );
              }
              this.animateParam.floorQueue.que = sortMapByKey(
                this.animateParam.floorQueue.que
              );
            } else {
              if (targetFloor < this.animateParam.floor - 1) {
                this.animateParam.floorQueue.que.set(
                  targetFloor,
                  intersect.object.name
                );
              } else {
                this.animateParam.floorQueue.inverse.set(
                  targetFloor,
                  intersect.object.name
                );
              }
              this.animateParam.floorQueue.que = sortMapByKey(
                this.animateParam.floorQueue.que,
                true
              );
            }
          } else {
            if (this.animateParam.indicatorDirection == 'Up') {
              if (targetFloor > this.animateParam.floor) {
                this.animateParam.floorQueue.que.set(
                  targetFloor,
                  intersect.object.name
                );
              } else {
                this.animateParam.floorQueue.inverse.set(
                  targetFloor,
                  intersect.object.name
                );
              }
              this.animateParam.floorQueue.que = sortMapByKey(
                this.animateParam.floorQueue.que
              );
            } else {
              if (targetFloor < this.animateParam.floor) {
                this.animateParam.floorQueue.que.set(
                  targetFloor,
                  intersect.object.name
                );
              } else {
                this.animateParam.floorQueue.inverse.set(
                  targetFloor,
                  intersect.object.name
                );
              }
              this.animateParam.floorQueue.que = sortMapByKey(
                this.animateParam.floorQueue.que,
                true
              );
            }
          }
        }
        // New queue
        else {
          this.animateParam.floorQueue.que.set(
            targetFloor,
            intersect.object.name
          );

          if (floorDiff > 0) {
            this.animateParam.indicatorDirection = 'Up';

            if (this.animateParam.state != 'Standby')
              this.playSound('assets/audios/EQUA14_5_Up.wav', true);
            this.animateParam.playedSounds.add(
              'assets/audios/EQUA14_89_Door_Open.wav'
            );
          } else if (floorDiff < 0) {
            this.animateParam.indicatorDirection = 'Down';

            if (this.animateParam.state != 'Standby')
              this.playSound('assets/audios/EQUA14_6_Down.wav', true);
            this.animateParam.playedSounds.add(
              'assets/audios/EQUA14_89_Door_Open.wav'
            );
          }
        }

        // Update to on material
        const onMat = this.scene.getMaterialByName(
          intersect.object.material.name.split('.')[0] + '_on'
        );
        if (!onMat) {
          console.warn(
            'Material not found: ' +
            intersect.object.material.name.split('.')[0]
          );
          return;
        }
        intersect.object.material = onMat;
        intersect.object.material.emissiveIntensity = 4;
        intersect.object.material.needsUpdate = true;

        // Load destinate indicator
        await this.setCageIndicator();
        const key = [...this.animateParam.floorQueue.que][0][0];
        if (this.animateParam.state != 'Open' && this.animateParam.state != 'Close') {
          await this.updateCageIndicatorMessage(key);
        }
        this.animateParam.floorTarget = key;
        this.animateParam.distance =
          (this.animateParam.floorTarget - this.animateParam.floorFrom) *
          this.animateParam.floorDistance;
      }

      if (this.animateParam.state == 'Standby') {
        // Force move
        this.animateParam.currentState = 'Close';
        this.animateParam.state = 'Close';
        let cageClose = this.mixer.clipAction(this.clips[1]);
        let hallClose =
          this.loadedModel.type == 'R9'
            ? this.mixer.clipAction(this.clips[2])
            : this.mixer.clipAction(this.clips[3]);
        cageClose.paused = hallClose.paused = true;
      }
    }

    // Cage control Buttons
    else {
      if (intersect.object.name.match(/_Open$/g)) {
        if (this.animateParam.state != 'Move') {
          this.animateParam.state = 'Open';
          this.animateParam.openedTime = 0;
        }
      } else if (intersect.object.name.match(/_Close$/g)) {
        if (this.animateParam.state == 'Open')
          this.animateParam.state = 'Close';
      } else if (intersect.object.name.match(/_[Ee]ncho$/g)) {
        if (this.animateParam.state != 'Move') {
          const wasOpen = this.animateParam.state == 'Open';
          this.animateParam.indicatorIsEncho = true;
          if (wasOpen) {
            this.animateParam.openedTime -= 10;
          } else {
            this.animateParam.state = 'Open';
            this.animateParam.openedTime = -10;
          }
          (async () => {
            await this.setCageIndicator();
          })();
        }
      } else if (intersect.object.name.match(/_[Pp]et_([Ss]ingle|[Tt]win)$/g)) {
        // Deactivate
        if (this.animateParam.petButton.isEnabled) {
          this.animateParam.petButton.isEnabled = false;
          this.animateParam.petButton.isFlicker = false;
          this.animateParam.petButton.activateTimer = 0;
          this.animateParam.petButton.flickerTimer = 0;
          this.mixer.timeScale = 1;

          const offMat = this.scene.getMaterialByName(
            intersect.object.material.name.replace('_on', '')
          );
          if (offMat) {
            intersect.object.material = offMat;
            intersect.object.material.needsUpdate = true;
          }
          return;
        }

        // Activate
        if (this.animateParam.state != 'Move') {
          this.animateParam.state = 'Open';
          this.animateParam.openedTime = -10;
        }
        this.animateParam.petButton.isEnabled = true;
        this.animateParam.petButton.isFlicker = true;
        return;
      } else if (intersect.object.name.match(/_Emergency$/g)) {
        this.animateParam.emergencyButton.isEnabled = true;
        this.animateParam.emergencyButton.activeTimer = 0;
      }

      // Update material
      if (!intersect.object.material.name.match(/_on$/g)) {
        const onMat = this.scene.getMaterialByName(
          intersect.object.material.name.split('.')[0] + '_on'
        );
        if (!onMat) {
          console.warn(
            'Material not found: ' +
            intersect.object.material.name.split('.')[0] +
            '_on'
          );
          return;
        }
        intersect.object.material = onMat;
        intersect.object.material.emissiveIntensity = 4;
        intersect.object.material.needsUpdate = true;
      }

      // Start timer
      let hasValue = false;
      for (let index = 0; index < this.animateParam.onButtons.length; index++) {
        if (this.animateParam.onButtons[index].object == intersect.object) {
          this.animateParam.onButtons[index].activatedTime = performance.now();
          hasValue = true;
        }
      }
      if (!hasValue) {
        this.animateParam.onButtons.push({
          object: intersect.object,
          activatedTime: performance.now(),
        });
      }
    }
  }

  onCagePatternCompare(id) {
    // Reset all buttons
    let buttonObjects = [];
    this.scene.getObjectByName('22_MainPanel_Button').traverse((obj) => {
      if (obj.type == 'Mesh') buttonObjects.push(obj);
    });
    if (this.scene.getObjectByName('27_SubPanelButton')) {
      this.scene.getObjectByName('27_SubPanelButton').traverse((obj) => {
        if (obj.type == 'Mesh') buttonObjects.push(obj);
      });
    }
    this.scene.getObjectByName('24_SidePanelButton_R').traverse((obj) => {
      if (obj.type == 'Mesh') buttonObjects.push(obj);
    });
    this.scene.getObjectByName('24_SidePanelButton_L').traverse((obj) => {
      if (obj.type == 'Mesh') buttonObjects.push(obj);
    });

    buttonObjects.forEach((obj) => {
      if (!obj.material) console.warn(obj);
      if (obj.material.name.match(/_on$/)) {
        const offMat = this.scene.getMaterialByName(
          obj.material.name.replace('_on', '')
        );
        if (offMat) {
          obj.material = offMat;
        }
      }
    });

    if (/^mainpanel_layout_[2-6]$/.test(id)) {
      // Compare queue
      for (let index = 7; index <= 12; index++) {
        if (this.animateParam.floorQueue.que.has(index))
          this.animateParam.floorQueue.que.delete(index);
        if (this.animateParam.floorQueue.inverse.has(index))
          this.animateParam.floorQueue.inverse.delete(index);
      }

      // cage position reset
      if (this.animateParam.floorTarget > 6 || this.animateParam.floor > 6) {
        this.animateParam.state = 'Standby';
        this.animateParam.currentState = 'Standby';
        this.animateParam.floorFrom = 1;
        this.animateParam.floor = 1;
        this.animateParam.floorTarget = 1;
        this.animateParam.floorQueue = {
          que: new Map(),
          inverse: new Map(),
        };
        this.animateParam.onInverse = false;
        this.animateParam.distance = 0;
        this.animateParam.movedDistance = 0;
        this.animateParam.checkPoints = [];
        this.animateParam.speed = 0;
        this.animateParam.hasPlayedSound = false;
        this.animateParam.currentAudioEl = null;
        this.animateParam.playedSounds = new Set();
        this.animateParam.openedTime = 0;
        this.animateParam.onButtons = [];
        this.animateParam.indicatorDirection = 'Up';

        // Delete hall if exists
        const hall = this.scene.getObjectByName('Hall');
        hall.position.y = this.animateParam.originY;
        const hallWrap = this.scene.getObjectByName('HallWrap');
        if (hallWrap) {
          hallWrap.clear(); // ToDo: memory performance
          hallWrap.removeFromParent();
        }

        (async () => {
          await this.setCageIndicator();
        })();
      }
    }

    // Restore floor button
    let activeFloors = new Set();
    this.animateParam.floorQueue.que.forEach((value, key) => {
      activeFloors.add(key);
    });
    this.animateParam.floorQueue.inverse.forEach((value, key) => {
      activeFloors.add(key);
    });

    buttonObjects.forEach((obj) => {
      if (obj.visible) {
        const floorstr = obj.name.match(/[0-9]{1,2}(.?|_Center)$/g);
        if (!floorstr) return;

        // per floor button
        let floorNum = Number(floorstr.pop().replace('_Center', ''));
        if (activeFloors.has(floorNum)) {
          // is active
          const onMat = this.scene.getMaterialByName(
            obj.material.name.split('.')[0] + '_on'
          );
          if (!onMat) {
            console.warn(
              'Material not found: ' + obj.material.name.split('.')[0] + '_on'
            );
            return;
          }
          obj.material = onMat;
          obj.material.emissiveIntensity = 4;
          obj.material.needsUpdate = true;
        }
      }
    });

    // Pet button
    let petbuttonObj;
    this.scene.getObjectByName('22_MainPanel_Button').traverseVisible((obj) => {
      if (obj.name.match(/_[Pp]et_([Ss]ingle|[Tt]win)$/g)) {
        petbuttonObj = obj;
      }
    });
    // Deactivate when not visible
    if (!petbuttonObj) {
      this.animateParam.petButton.isEnabled = false;
      this.animateParam.petButton.isFlicker = false;
      this.animateParam.petButton.activateTimer = 0;
      this.animateParam.petButton.flickerTimer = 0;
      this.mixer.timeScale = 1;
    } else if (this.animateParam.petButton.isEnabled) {
      const onMat = this.scene.getMaterialByName(
        petbuttonObj.material.name.split('.')[0] + '_on'
      );
      if (!onMat) {
        console.warn(
          'Material not found: ' +
          petbuttonObj.material.name.split('.')[0] +
          '_on'
        );
        return;
      }
      petbuttonObj.material = onMat;
      petbuttonObj.material.emissiveIntensity = 4;
      petbuttonObj.material.needsUpdate = true;
    }
  }

  //////////
  // Hall
  //////////
  animateHall(dt) {
    if (!this.hasInitialized)
      (async () => {
        this.hasInitialized = true;
        await this.updateHallDigitalIndicators();
        await this.updateHallLCDIndicators(true);
      })();

    // State changed
    if (this.animateParam.state != this.animateParam.currentState) {
      this.onHallStateChanged();
    }

    // Check per frame
    this.onHallPerFrame(dt);

    // Restore buttons material
    this.restoreButton();
  }

  //
  // Hall Animation Controls
  //
  onHallStateChanged() {
    switch (this.animateParam.state) {
      case 'Open': {
        let doorOpens, doorCloses;
        switch (this.loadedModel.type) {
          case 'R9':
            doorOpens = [
              this.mixer.clipAction(this.clips[1]),
              this.mixer.clipAction(this.clips[2]),
            ];
            doorCloses = [
              this.mixer.clipAction(this.clips[0]),
              this.mixer.clipAction(this.clips[3]),
            ];
            break;
          default:
            doorOpens = [
              this.mixer.clipAction(this.clips[0]),
              this.mixer.clipAction(this.clips[2]),
            ];
            doorCloses = [
              this.mixer.clipAction(this.clips[1]),
              this.mixer.clipAction(this.clips[3]),
            ];
            break;
        }

        // Reset Close Animation if finished
        if (doorCloses[0].paused) {
          doorCloses.forEach((doorClose) => {
            doorClose.stop();
            this.state.actionStates[doorClose.getClip().name] = false;
          });
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_4_Door_Open.wav'
          );
        }

        // Play Open Animation
        doorOpens.forEach((doorOpen) => {
          doorOpen.setLoop(LoopOnce);
          doorOpen.clampWhenFinished = true;
          doorOpen.paused = true;
          doorOpen.reset().play();
          this.state.actionStates[doorOpen.getClip().name] = true;
        });

        // On closing door
        if (doorCloses[0].isRunning()) {
          doorCloses.forEach((doorClose, index) => {
            doorClose.crossFadeTo(doorOpens[index], 1.3);
            this.state.actionStates[doorClose.getClip().name] = false;
          });

          // Deactivate door signal
          this.animateParam.doorSignal.isActive = false;
        }

        this.playSound('assets/audios/EQUA14_89_Door_Open.wav', true);
        break;
      }

      case 'Close': {
        let doorOpens, doorCloses;
        switch (this.loadedModel.type) {
          case 'R9':
            doorOpens = [
              this.mixer.clipAction(this.clips[1]),
              this.mixer.clipAction(this.clips[2]),
            ];
            doorCloses = [
              this.mixer.clipAction(this.clips[0]),
              this.mixer.clipAction(this.clips[3]),
            ];
            break;
          default:
            doorOpens = [
              this.mixer.clipAction(this.clips[0]),
              this.mixer.clipAction(this.clips[2]),
            ];
            doorCloses = [
              this.mixer.clipAction(this.clips[1]),
              this.mixer.clipAction(this.clips[3]),
            ];
            break;
        }

        // Reset Open Animation if finished
        if (doorOpens[0].paused) {
          doorOpens.forEach((doorOpen) => {
            doorOpen.stop();
            this.state.actionStates[doorOpen.getClip().name] = false;
          });
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_4_Door_Close.wav'
          );
        }

        // Play Close Animation
        doorCloses.forEach((doorClose) => {
          doorClose.setLoop(LoopOnce);
          doorClose.clampWhenFinished = true;
          doorClose.paused = true;
          doorClose.reset().play();
          this.state.actionStates[doorClose.getClip().name] = true;
        });

        // Activate door signal
        this.animateParam.doorSignal.isActive = true;
        this.animateParam.doorSignal.activeTimer = 0;

        // On opening doors
        if (doorOpens[0].isRunning()) {
          doorOpens.forEach((doorOpen, index) => {
            doorOpen.crossFadeTo(doorCloses[index], 1.3);
            this.state.actionStates[doorOpen.getClip().name] = false;
          });
        }

        this.playSound('assets/audios/EQUA14_4_Door_Close.wav', true);
        break;
      }
    }

    this.animateParam.currentState = this.animateParam.state;
  }

  onHallPerFrame(dt) {
    if (this.animateParam.state == 'Open') {
      // Auto close timer
      this.animateParam.openedTime += dt;
      if (this.animateParam.openedTime > 6) {
        this.animateParam.state = 'Close';
      }
      // Activate door signal
      else if (
        this.animateParam.openedTime > 5 &&
        !this.animateParam.doorSignal.isActive
      ) {
        this.animateParam.doorSignal.isActive = true;
      }
      // Play sound if it has inverse queue
      else if (
        this.animateParam.openedTime > 3 &&
        this.animateParam.onInverse
      ) {
        if (this.animateParam.indicatorDirection == 'Up') {
          this.playSound('assets/audios/EQUA14_5_Up.wav', true);
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_6_Down.wav'
          );
        } else {
          this.playSound('assets/audios/EQUA14_6_Down.wav', true);
          this.animateParam.playedSounds.delete(
            'assets/audios/EQUA14_5_Up.wav'
          );
        }
        this.animateParam.onInverse = false;
      }
    } else if (this.animateParam.state == 'Close') {
      this.animateParam.openedTime = 0;

      // Change state to "Standby" when close animation finished
      let doorClose =
        this.loadedModel.type == 'R9'
          ? this.mixer.clipAction(this.clips[0])
          : this.mixer.clipAction(this.clips[1]);
      if (doorClose.paused) {
        this.animateParam.state = 'Standby';
        this.animateParam.playedSounds = new Set();

        this.animateParam.isLanternOn = false;
        this.updateHallLanterns();

        (async () => {
          await this.updateHallDigitalIndicators();
          await this.updateHallLCDIndicators();
        })();
      }
    }

    // Door Signal controller
    this.updateDoorSignal(dt);
  }

  async onHallIntersect(intersect) {
    this.animateParam.state = 'Open';
    this.animateParam.openedTime = 0;

    const direction = intersect.object.name.match(
      /_[1-2]_(up|down)(.?|_Tandoku|_SA)$/g
    )[0];
    if (direction.includes('_up')) {
      this.animateParam.indicatorDirection = 'Up';
    } else {
      this.animateParam.indicatorDirection = 'Down';
    }

    await this.updateHallDigitalIndicators();
    await this.updateHallLCDIndicators();

    // Update button material
    if (!intersect.object.material.name.match(/_on$/g)) {
      const onMat = this.scene.getMaterialByName(
        intersect.object.material.name.split('.')[0] + '_on'
      );
      if (!onMat) {
        console.warn(
          'Material not found: ' +
          intersect.object.material.name.split('.')[0] +
          '_on'
        );
      } else {
        intersect.object.material = onMat;
        intersect.object.material.emissiveIntensity = 4;
        intersect.object.material.needsUpdate = true;
      }
    }

    // Activate lantern
    this.animateParam.isLanternOn = true;
    this.updateHallLanterns();

    // Start timer
    let hasValue = false;
    for (let index = 0; index < this.animateParam.onButtons.length; index++) {
      if (this.animateParam.onButtons[index].object == intersect.object) {
        this.animateParam.onButtons[index].activatedTime = performance.now();
        hasValue = true;
      }
    }
    if (!hasValue) {
      this.animateParam.onButtons.push({
        object: intersect.object,
        activatedTime: performance.now(),
      });
    }
  }

  //
  // Hall Animation Functions
  //
  async updateHallDigitalIndicators() {
    const floorMat = this.scene.getMaterialByName(
      'M_HallIndicator_Digital_Floor'
    );
    const arrowMat = this.scene.getMaterialByName(
      'M_HallIndicator_Digital_Arrow'
    );

    if (this.animateParam.state == 'Standby') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Off.png'
      );
      map.flipY = false;
      arrowMat.map = arrowMat.emissiveMap = map;
      arrowMat.emissive = 0;
    } else if (this.animateParam.indicatorDirection == 'Up') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Arrow_Up.png'
      );
      map.flipY = false;
      arrowMat.map = arrowMat.emissiveMap = map;
      arrowMat.emissive = 0;
    } else {
      const map = await new TextureLoader().loadAsync(
        'assets/images/Indicator_SidePanel/Indicator_Side_Arrow_Down.png'
      );
      map.flipY = false;
      arrowMat.map = arrowMat.emissiveMap = map;
      arrowMat.emissive = 0;
    }
    arrowMat.needsUpdate = true;

    const map = await new TextureLoader().loadAsync(
      'assets/images/Indicator_SidePanel/Indicator_Side_01.png'
    );
    map.flipY = false;
    floorMat.map = floorMat.emissiveMap = map;
    floorMat.emissive = 0;
    floorMat.needsUpdate = true;
  }

  async updateHallLCDIndicators(init = false) {
    const UpObjects = [
      this.scene.getObjectByName(
        this.loadedModel.type + '_HallIndicator_LCD_White_Up'
      ),
      this.scene.getObjectByName(
        this.loadedModel.type + '_HallIndicator_LCD_White_Up_Tandoku'
      ),
    ];
    const DownObjects = [
      this.scene.getObjectByName(
        this.loadedModel.type + '_HallIndicator_LCD_Down'
      ),
      this.scene.getObjectByName(
        this.loadedModel.type + '_HallIndicator_LCD_Down_Tandoku'
      ),
    ];

    // Initialize visible
    if (init) {
      UpObjects.forEach((obj) => {
        if (obj) obj.visible = true;
      });
      DownObjects.forEach((obj) => {
        if (obj) obj.visible = false;
      });
    }

    const floorMat = this.scene.getMaterialByName('M_HallIndicator_LCD_Floor');
    const arrowMat = this.scene.getMaterialByName('M_HallIndicator_LCD_Arrow');

    if (this.animateParam.state == 'Standby') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/HallIndicator_LCD_' +
        this.animateParam.indicatorColor +
        '/HallInd_LCD_' +
        this.animateParam.indicatorColor +
        '_null.png'
      );
      map.flipY = false;
      arrowMat.map = map;
    } else if (this.animateParam.indicatorDirection == 'Up') {
      const map = await new TextureLoader().loadAsync(
        'assets/images/HallIndicator_LCD_' +
        this.animateParam.indicatorColor +
        '/HallInd_LCD_' +
        this.animateParam.indicatorColor +
        '_Arrow.png'
      );
      map.flipY = false;
      map.center = new Vector2(0.5, 0.5);
      map.rotation = 0;
      arrowMat.map = map;

      UpObjects.forEach((obj) => {
        if (obj) obj.visible = true;
      });
      DownObjects.forEach((obj) => {
        if (obj) obj.visible = false;
      });
    } else {
      const map = await new TextureLoader().loadAsync(
        'assets/images/HallIndicator_LCD_' +
        this.animateParam.indicatorColor +
        '/HallInd_LCD_' +
        this.animateParam.indicatorColor +
        '_Arrow.png'
      );
      map.flipY = false;
      map.center = new Vector2(0.5, 0.5);
      map.rotation = Math.PI;
      arrowMat.map = map;

      UpObjects.forEach((obj) => {
        if (obj) obj.visible = false;
      });
      DownObjects.forEach((obj) => {
        if (obj) obj.visible = true;
      });
    }
    arrowMat.needsUpdate = true;

    const map = await new TextureLoader().loadAsync(
      'assets/images/HallIndicator_LCD_' +
      this.animateParam.indicatorColor +
      '/HallInd_LCD_' +
      this.animateParam.indicatorColor +
      '_01.png'
    );
    map.flipY = false;
    floorMat.map = map;
    floorMat.needsUpdate = true;

    let coverMat = null;
    if (this.animateParam.indicatorColor == 'White') {
      coverMat = this.scene.getMaterialByName('M_IndicatorPanel_GrayishWhite');
    } else {
      coverMat = this.scene.getMaterialByName('M_IndicatorPanel_black');
    }
    if (!coverMat) {
      console.error('Indicator material is not found.');
      return;
    }
    const indRoot = this.scene.getObjectByName('64_HallIndicatorPanel');
    indRoot.children.forEach(indObj => {
      if (/IndicatorPanel_(Black|White)_LCD(_Tandoku)?$/.test(indObj.name)) {
        indObj.material = coverMat;
      }
    })
  }

  updateHallLanterns() {
    // Reset
    this.scene.getObjectByName('68_HallLantern').traverse((obj) => {
      if (/_(up|down)$/.test(obj.name)) {
        const offMat = this.scene.getMaterialByName('M_CoverGlass');
        obj.material = offMat;
      }
    });

    // ON
    if (this.animateParam.isLanternOn) {
      this.scene.getObjectByName('68_HallLantern').traverseVisible((obj) => {
        let onMat;
        if (this.animateParam.lanternColor == 'Orange') {
          onMat = this.scene.getMaterialByName('M_Lantern_ON_Orange');
        } else {
          onMat = this.scene.getMaterialByName('M_Lantern_ON_White');
        }

        if (
          this.animateParam.indicatorDirection == 'Up' &&
          /_up$/.test(obj.name)
        ) {
          obj.material = onMat;
        } else if (
          this.animateParam.indicatorDirection == 'Down' &&
          /_down$/.test(obj.name)
        ) {
          obj.material = onMat;
        }
      });
    }
  }

  // // // //
  // Common Animation Functions
  // // // //
  restoreButton() {
    this.animateParam.onButtons.forEach((button, i) => {
      if (performance.now() - button.activatedTime > 1000) {
        const offMat = this.scene.getMaterialByName(
          button.object.material.name.replace('_on', '')
        );
        if (!offMat) {
          console.warn(
            'Material not found: ' +
            button.object.material.name.replace('_on', '')
          );
          return;
        }
        button.object.material = offMat;
        button.object.material.needsUpdate = true;

        this.animateParam.onButtons.splice(i, 1);
      }
    });
  }

  playSound(path, avoidConflict = false) {
    let audioEl;
    if (avoidConflict && !this.animateParam.playedSounds.has(path)) {
      if (this.animateParam.currentAudioEl)
        this.animateParam.currentAudioEl.pause();
      audioEl = new Audio(path);
      audioEl.autoplay = true;
      if (!this.isSoundEnable) audioEl.volume = 0;
      this.animateParam.currentAudioEl = audioEl;
      this.animateParam.playedSounds.add(path);

      if (
        path == 'assets/audios/EQUA14_5_Up.wav' &&
        this.animateParam.playedSounds.has('assets/audios/EQUA14_6_Down.wav')
      )
        this.animateParam.playedSounds.delete(
          'assets/audios/EQUA14_6_Down.wav'
        );
      else if (
        path == 'assets/audios/EQUA14_6_Down.wav' &&
        this.animateParam.playedSounds.has('assets/audios/EQUA14_5_Up.wav')
      )
        this.animateParam.playedSounds.delete('assets/audios/EQUA14_5_Up.wav');
    } else if (!avoidConflict) {
      if (this.animateParam.currentAudioEl)
        this.animateParam.currentAudioEl.pause();
      audioEl = new Audio(path);
      audioEl.autoplay = true;
      if (!this.isSoundEnable) audioEl.volume = 0;
      this.animateParam.currentAudioEl = audioEl;
    }

    return audioEl;
  }

  toggleSound(e) {
    this.isSoundEnable = !this.isSoundEnable;
    if (this.isSoundEnable)
      e.target.src = 'assets/images/01_layout/lay_btn3_on.png';
    else e.target.src = 'assets/images/01_layout/lay_btn3_off.png';
  }

  updateDoorSignal(dt) {
    if (this.animateParam.doorSignal.isEnabled) {
      let sigObj = this.scene.getObjectByName('16_Door_Signal').children[0];

      // // // //
      // Animate
      // // // //
      if (this.animateParam.doorSignal.isActive) {
        this.animateParam.doorSignal.activeTimer += dt;
        this.animateParam.doorSignal.blinkTimer += dt;

        // OFF
        if (
          0.64 >= this.animateParam.doorSignal.blinkTimer &&
          this.animateParam.doorSignal.blinkTimer > 0.32
        ) {
          const offMat = this.scene.getMaterialByName('M_DoorSignal_off');
          if (offMat) {
            sigObj.material = offMat;
            sigObj.material.needsUpdate = true;
          }
        }

        // ON
        else {
          if (this.animateParam.doorSignal.blinkTimer > 0.64)
            this.animateParam.doorSignal.blinkTimer = 0;
          const onMat = this.scene.getMaterialByName('M_DoorSignal_on');
          if (onMat) {
            sigObj.material = onMat;
            sigObj.material.needsUpdate = true;
          }
        }

        // // // //
        // Auto turn off
        // // // //
        const triggerTime = this.loadedModel.type == 'P11' ? 1.6 : 3.5;
        if (this.animateParam.doorSignal.activeTimer > triggerTime) {
          this.animateParam.doorSignal.isActive = false;
        }
      }

      // // // //
      // Off
      // // // //
      else {
        if (
          this.animateParam.doorSignal.activeTimer != 0 ||
          this.animateParam.doorSignal.blinkTimer != 0
        ) {
          this.animateParam.doorSignal.activeTimer =
            this.animateParam.doorSignal.blinkTimer = 0;

          const offMat = this.scene.getMaterialByName('M_DoorSignal_off');
          if (offMat) {
            sigObj.material = offMat;
            sigObj.material.needsUpdate = true;
          }
        }
      }
    }
  }

  updateEmergency(dt) {
    const ipPanel = this.scene.getObjectByName("MP_Interphone_ON");
    if (!ipPanel) {
      console.error("'MP_Interphone_ON' is not found.");
      this.animateParam.emergencyButton.isEnabled = false;
      return;
    }

    if (this.animateParam.emergencyButton.activeTimer == 0) {
      const ipCallMat = this.scene.getMaterialByName("M_MP_Interphone_Call");
      if (!ipCallMat) {
        console.error("'M_MP_Interphone_Call' is not found.");
        this.animateParam.emergencyButton.isEnabled = false;
        return;
      }

      ipPanel.material = ipCallMat;
      ipPanel.material.needsUpdate = true;
    } else if (this.animateParam.emergencyButton.activeTimer >= 5) {
      const ipOffMat = this.scene.getMaterialByName("M_MP_Interphone");
      if (!ipOffMat) {
        console.error("'M_MP_Interphone' is not found.");
        this.animateParam.emergencyButton.isEnabled = false;
        return;
      }

      ipPanel.material = ipOffMat;
      ipPanel.material.needsUpdate = true;

      this.animateParam.emergencyButton.isEnabled = false;
      return;
    }

    this.animateParam.emergencyButton.activeTimer += dt;
  }
}

function sortMapByKey(map, inverse = false) {
  const inv = inverse ? -1 : 1;
  return new Map(
    [...map.entries()].sort((a, b) => (a[0] > b[0] ? 1 * inv : -1 * inv))
  );
}
